import { Box, Typography } from '@mui/material';

/** 구간 번호 → ❶❷❸...⓴ 표기 */
const STEP_CIRCLED = '❶❷❸❹❺❻❼❽❾❿⓫⓬⓭⓮⓯⓰⓱⓲⓳⓴';

function stepToCircled(n: number): string {
  return n >= 1 && n <= STEP_CIRCLED.length ? STEP_CIRCLED[n - 1] ?? String(n) : String(n);
}

export type RouletteKpiCardData = {
  totalEventCount: number;
  finalConversionRate: number;
  exitSectionFrom: number;
  exitSectionTo: number;
  exitSectionRate: number;
};

type Props = {
  data: RouletteKpiCardData;
};

export default function RouletteKpiCard({ data }: Props) {
  const {
    totalEventCount,
    finalConversionRate,
    exitSectionFrom,
    exitSectionTo,
    exitSectionRate,
  } = data;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 300px))',
          md: 'repeat(3, minmax(0, 300px))',
        },
        gap: 1.5,
        mt: 3,
      }}
    >
      {/* 조회된 총 횟수: 선택 콘텐츠·기간 기준, 해당 콘텐츠 첫 단계 이벤트 수 (중복 포함) */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold', fontSize: '0.8rem' }}>
          조회된 총 횟수
        </Typography>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
          {totalEventCount.toLocaleString('ko-KR')}
        </Typography>
      </Box>

      {/* 최종 전환율: 마지막 단계까지 도달 횟수 / 조회된 총 횟수 */}
      <Box
        sx={{
          backgroundColor: '#EFF6FF',
          border: '1px solid #2563EB',
          borderRadius: 2,
          p: 1.5,
          color: '#fff',
        }}
      >
        <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(0, 0, 0, 0.7)', fontWeight: 'bold', fontSize: '0.8rem' }}>
          최종 전환율
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="#000000ff" sx={{ fontSize: '1rem' }}>
          {finalConversionRate.toFixed(2)}%
        </Typography>
      </Box>

      {/* 가장 많은 이탈 구간: 첫 단계→마지막 단계 중 전환율이 가장 크게 떨어진 구간 */}
      <Box
        sx={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #EF4444',
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ mb: 1, color: 'rgba(0, 0, 0, 0.7)', fontWeight: 'bold', fontSize: '0.8rem' }}>
          가장 많은 이탈 구간
        </Typography>
        {exitSectionFrom > 0 && exitSectionTo > 0 ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              flexWrap: 'wrap',
            }}
          >
            <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {stepToCircled(exitSectionFrom)}
            </Typography>
            <Box component="span" sx={{ color: '#374151', fontWeight: 600, fontSize: '1rem' }}>
              →
            </Box>
            <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {stepToCircled(exitSectionTo)}
            </Typography>
            <Box component="span" sx={{ color: '#DC2626', fontSize: '1.25rem', lineHeight: 1 }}>
              ▼
            </Box>
            <Typography component="span" sx={{ color: '#DC2626', fontWeight: 700, fontSize: '1rem' }}>
              {Math.abs(exitSectionRate).toFixed(1)}%
            </Typography>
          </Box>
        ) : (
          <Typography variant="h6" fontWeight="bold" color="text.secondary" sx={{ fontSize: '1rem' }}>
            -
          </Typography>
        )}
      </Box>
    </Box>
  );
}

